import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { Location } from "@angular/common";
import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { ActivatedRoute, ParamMap } from "@angular/router";
import { environment } from "@env/environment";
import {
  amount,
  generateCheckDigit,
  JobDto,
  JobLineDto,
  JobStatusGrouping as StatusGroup,
  JobVat,
  ModelMasterDto,
  unpaidVat
} from "@shared/models";
import { Observable } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { convertToDate, Disposable } from "../../common";
import { AppQuery } from "../../common/app.store";
import { AuthenticationService, JobService, ProductSettingService } from "../../common/services";

@Component({
  selector: "abi-cod",
  templateUrl: "./cod.component.html",
  styleUrls: ["./cod.component.scss"]
})
export class CodComponent extends Disposable implements OnInit {
  model: ModelMasterDto;
  codEnv = environment.cod;
  vatMask = "0000000000";
  @ViewChild("paidDialog") paidDialog: any;
  @Input() useRouting = true;
  @Input() check: string;
  @Input() get boundModel(): ModelMasterDto {
    return this.model;
  }
  set boundModel(value: ModelMasterDto) {
    if (this.model !== value && value) {
      this.displayAccountDetails(value);
    }
  }
  @Input() get boundJob(): JobDto {
    return this.job;
  }
  set boundJob(value: JobDto) {
    if (this.job !== value && value) {
      this.displayJob(value);
    }
  }

  adumoAppId: string;
  adumoToken: string;
  job: JobDto;
  posting = false;
  extraReference = "";
  postbackUrl: Observable<string>;
  payment: FormGroup;

  tenant: string;
  editVat = false;
  vatNumber: string;
  companyName: string;
  companyAccountName: string;
  companyAccountNumber: string;
  companyBankName: string;
  companyBankBranch: string;

  statusId = "";
  prePayment = 1;

  confirmMsg = ["JobCod.CancelConfirmMessage"];

  termsAndConditions: Observable<SafeHtml>;
  privacyNotice: Observable<SafeHtml>;
  dialogText: string;// can be html text if needs be

  isMobile: Observable<boolean>;

  get brand(){
    return this.model?.categories[0];
  }

  constructor(
    private route: ActivatedRoute,
    public jobService: JobService,
    public productSettings: ProductSettingService,
    private location: Location,
    private responsive: BreakpointObserver,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog,
    private authService: AuthenticationService,
    private appQuery: AppQuery, private formBuilder: FormBuilder
  ) {
    super();

    this.payment = formBuilder.group({
      paidDate: [Date.today(), Validators.required],
      paid: [0, Validators.required],
    });

    this.isMobile = this.responsive.observe(Breakpoints.Handset).pipe(map(o => o.matches));
    JobVat.vatRate = productSettings.numericValue("VatIncluded") ? 0.0 : 0.15;

    this.adumoAppId = productSettings.stringValue("AdumoApplicationID");
    this.prePayment = productSettings.stringValue("InvoiceMethod") === "OnPayment" ? 1 : 0;

    this.companyAccountName = productSettings.stringValue("CompanyAccountName");
    this.companyAccountNumber = productSettings.stringValue("CompanyAccountNumber");
    this.companyBankName = productSettings.stringValue("CompanyBankName");
    this.companyBankBranch = productSettings.stringValue("CompanyBankBranch");
  }

  ngOnInit() {
    this.termsAndConditions = this.getText("TermsAndConditions");
    this.privacyNotice = this.getText("PrivacyNotice");
    this.tenant = this.route.snapshot.paramMap.get("tenant");
    this.check = this.check || this.route.snapshot.paramMap.get("check");
    this.tenant = this.tenant.split("-", 1)[0] || this.tenant;

    if (this.useRouting) {
      this.route.paramMap
        .pipe(
          switchMap((params: ParamMap) => this.jobService.getJob(params.get("jobId"), params.get("check")))
        )
        .subscribe((job: JobDto) => this.displayJob(job),
          (error) => { this.location.back(); }
        );
    }

    this.postbackUrl = this.appQuery.$tenant2.pipe(map(tenant => {
      if (this.job) {
        const root = typeof (window) !== "undefined" ? window.location.origin : "";
        return `${root}/api/${tenant}/payments/job/${this.job.jobId}/`;
      }
      return "";
    }));

  }

  getText(id: string): Observable<SafeHtml> {
    return this.productSettings.getText(id).pipe(map((txt) => this.sanitizer.bypassSecurityTrustHtml(txt)));
  }

  vatDetailsChanged = false;
  updateJob(event) {
    this.jobService
      .updateJobLite(this.job.jobId, this.check || generateCheckDigit(this.job.jobId), {
        companyName: this.companyName,
        vatNumber: this.editVat ? this.vatNumber : "",
        statusId: this.statusId,
        amount: this.unpaid,
      })
      .toPromise()
      .then((x) => {
        this.vatDetailsChanged = false;
        this.job.contact.companyName = this.companyName;// Display Company Name in the UI
       });
  }

  getSetting(setting: string){
    return this.productSettings.stringValue(setting);
  }

  cancelJob(dialog: any) {
    //    if (this.ca) { return; }

    this.confirmMsg = ["JobCod.CancelConfirmMessage"];
    const ref = this.dialog.open(dialog);
    // certain lines may need to be paid outright - hoever full quote may not be acceptable (this is the FIN)
    const newStatus = this.job.lines.some(l => !!l.paid)
    ? this.getSetting('CODNotAcceptPaidJobStatus') || "FIN"
    : this.getSetting('CODNotAcceptUnPaidJobStatus') || "CXC";
    ref.afterClosed().subscribe((confirm: boolean) => {
      if (confirm) {
        this.jobService
        .updateJobLite(this.job.jobId, this.check, {
          statusId: newStatus,
        })
        .toPromise()
        .then((x) => {
          this.job.statusId = newStatus;
        });
      }
    });
  }

  acceptJob(dialog: any) {
    if (this.accepted()) { return; }

    this.confirmMsg = ["JobCod.AcceptConfirmMessage", "JobCod.AcceptConfirmMessage2"];
    const newStatus = this.job.lines.some(l => l.typeId === "STK")
    ? this.getSetting('CODAcceptWithStockJobStatus') || "PARTREQ"
    : this.getSetting('CODAcceptWithoutStockJobStatus') || "TECH";
    const ref = this.dialog.open(dialog);
    ref.afterClosed().subscribe((ok: boolean) => {
      if (ok) {
        this.jobService
          .updateJobLite(this.job.jobId, this.check, {
            statusId: newStatus,

          })
          .toPromise()
          .then((x) => {
            this.job.statusId = newStatus;
            // window.close();
          });
      }
    });
  }

  private displayJob(job: JobDto) {
    this.job = job;
    this.companyName = job.contact?.companyName;// || job.contact?.note;
    this.vatNumber = job.contact?.instantMessageId;
    this.editVat = !!this.vatNumber;
  }

  displayAccountDetails(model: ModelMasterDto){
    this.model = model;
    // Brand should now exist
    // Check for Brand specific Account details
    this.companyAccountName = this.productSettings.stringValue(`CompanyAccountName_${this.brand}`) || this.companyAccountName;
    this.companyAccountNumber = this.productSettings.stringValue(`CompanyAccountNumber_${this.brand}`) || this.companyAccountNumber;
    this.companyBankName = this.productSettings.stringValue(`CompanyBankName_${this.brand}`) || this.companyBankName;
    this.companyBankBranch = this.productSettings.stringValue(`CompanyBankBranch_${this.brand}`) || this.companyBankBranch;
  }

  saveChanges() { }

  cancelChanges() {
    this.displayJob(this.job);
    this.location.back();
  }

  get total(): number {
    return this.job ? this.job.lines.reduce((amt: number, line) => amt + amount(line), 0) : 0;
  }

  // CODE SMELL: vat rate to be configured in DB setting
  get vat(): number {
    const total = this.total;
    return total / 1.15 * 0.15;
  }

  get unpaid(): number {
    return this.job ? unpaidVat(this.job) : 0;
  }

  get ordered(): number {
    return this.job ? this.job.lines.reduce((ord: number, line) => ord + (line.paid ? line.quantityOrdered : 0), 0) : 0;
  }

  // Not a pre-payment job || isnt Cancelled & Closed
  canPay(): boolean {
    return this.adumoAppId
    && this.job
    && !StatusGroup.CANCELLED.includes(this.job.statusId)
    && this.job.lines.some(l => !l.paid && (!!l.invoice || l.mode === "Pre"));
  }

  canCancel(): boolean {
    return this.job
    && this.job.closed === 0
    && !this.job.lines.some(l => !!l.invoice)
    && StatusGroup.CAN_CANCEL.includes(this.job.statusId);
  }

  canAccept(): boolean {
    return this.job
    && StatusGroup.CAN_ACCEPT.includes(this.job.statusId)
    && this.job.lines.some(l => !l.paid && !this.prePayment && l.mode !== "Pre" && !l.invoice)
    && this.job.closed === 0;
  }

  accepted(): boolean {
    return this.job
    && StatusGroup.ACCEPTED.includes(this.job.statusId)
    && this.job.lines.some(l => !l.paid && !this.prePayment
    && l.mode !== "Pre" && !l.invoice);
  }

  notAccepted(): boolean {
    return this.job
    && (this.job.statusId === "FIN" || this.job.statusId === "CXC")
    /*&& this.job.lines.some(l => !l.paid && !this.prePayment &&
        l.mode !== "Pre" && !l.invoice)*/;
  }

  hasLabourLine(){
    const unpaid = this.job.lines; // .filter(l => !l.invoice);
    return unpaid.some((l) => l.typeId === "LAB" && l.itemId === "LabCall");
  }

  cleanTelephone(): string {
    return this.job ? this.job.contact.mobile.replace(/\D+/g, "") : "";
  }

  showWarrantyText(dialog: any) {
    this.dialogText = "JobCod.WarrantyText";
    this.dialog.open(dialog);
    return false;
  }

  showText(dialog: any, terms: boolean) {
    this.dialogText = terms ? "JobCod.TermsAndConditionsText" : "JobCod.PrivacyNoticeText";
    this.dialog.open(dialog);
    return false;
  }

  availableText(line: JobLineDto): string {
    if (line.paid) {
      return "";
    }
    return line.quantityDelivered ? "Now = " + line.quantityDelivered : line.line2;
  }

  makePayment(form: any) {
    this.posting = true;
    this.jobService.updateJobLite(this.job.jobId, this.check, {
      companyName: this.companyName,
      vatNumber: this.editVat ? this.vatNumber : "",
      statusId: this.statusId,
      amount: this.unpaid,
    }).toPromise().then(ok => {
      if (ok.allowPayment) {
        if (ok.receipt) {
          this.extraReference = "__" + ok.receipt;
        }
        this.jobService.getAdumoJwt(this.unpaid, this.paymentReference()).toPromise().then(jwt => {
          this.adumoToken = jwt;
          setTimeout(() => form.submit(), 5);
        });
      } else {
        this.posting = false;
      }
    });
  }

  refundJob(dialog: any) {
    this.confirmMsg = ["JobCod.AcceptRefundMessage"];
    const ref = this.dialog.open(dialog);
    ref.afterClosed().subscribe((cancel: boolean) => {
      if (cancel) {
        this.jobService.refundJob(this.job.jobId, this.check).toPromise().then(j => {
          this.boundJob = j;
        });
      }
    });
  }

  paymentReference(): string {
    return this.job ? this.job.jobId + this.extraReference : "";
  }

  markPaid() {
    this.productSettings.getCounter("JobInvoice").toPromise().then(cnt => {
      const oldValue = this.payment.value;
      this.payment.reset({
        paid: this.unpaid,
        paidDate: Date.today()
      });
      const ref = this.dialog.open(this.paidDialog);
      ref.afterClosed().subscribe((result: boolean) => {
        if (!result) {
          this.payment.reset(oldValue);
        } else {
          const paid = this.payment.value.paid as number;
          const paidDate = convertToDate(this.payment.value.paidDate);
          this.jobService.updateJobLite(this.job.jobId, this.check, {
            paid,
            paidDate

          }).toPromise().then(j => {
            for (const line of this.job.lines) {
              line.invoice = j.invoice;
              line.receipt = j.receipt;
              line.paid = line.price;
            }
            this.job.paid = this.job.unpaid;
            this.job.unpaid = 0;

          });
        }
      });
    });
  }

}
